/**
 * CTC (Commodity Trading Center) purchase-order lifecycle.
 *
 * Pure, side-effect free port of the legacy Camunda `POWF` process as reconstructed from
 * KFH-NEW-CTC (`PurchaseOrderStatusEnum`, `PurchaseOrderLifecycleMatrix`, and the branch
 * points in `PurchaseOrderController`). Shared by the webapp (to render the action bar and
 * the work queue) and by `functions-ctc` (the only place that writes transitions).
 *
 * Branching is driven by the customer's workflow flags, which the legacy app passed to
 * Camunda as process variables. Legacy status ids are kept next to each code so the
 * migration script and anyone reading the old database can map both ways.
 *
 * Deliberate departures from the legacy flow:
 *  - No "pending broker assignment" vs "broker assigned" split (legacy 5/6/7 were three
 *    Camunda tasks under one aggregate status): acceptance assigns the dealer, the dealer
 *    then picks the broker and confirms the purchase in one screen.
 *  - Beneficiary "collect" is an explicit state (legacy showed a "contact CTC" dialog and
 *    left the PO parked on the sell task).
 *  - "Cancel with justification" (legacy 40/41) is dropped: it had no backend endpoint.
 */

export enum CtcPoStatusCode {
  draft = "draft", // legacy 1
  waiting_customer_approval = "waiting_customer_approval", // legacy 2/3 (submitted, checker lane)
  waiting_ctc_acceptance = "waiting_ctc_acceptance", // legacy 4
  waiting_purchase_confirmation = "waiting_purchase_confirmation", // legacy 5/6/7
  waiting_beneficiary_transfer_approval = "waiting_beneficiary_transfer_approval", // legacy 13 (WhatsApp)
  waiting_customer_transfer_request = "waiting_customer_transfer_request", // legacy 8
  waiting_customer_transfer_approval = "waiting_customer_transfer_approval", // legacy 10
  waiting_transfer_confirmation = "waiting_transfer_confirmation", // legacy 9
  waiting_beneficiary_sell_request = "waiting_beneficiary_sell_request", // legacy 30 (WhatsApp)
  waiting_sell_request = "waiting_sell_request", // legacy 11 (customer sells on behalf)
  waiting_sell_confirmation = "waiting_sell_confirmation", // legacy 12
  waiting_collection = "waiting_collection", // new: beneficiary chose to collect
  completed = "completed", // legacy 31
  rejected_by_customer = "rejected_by_customer", // legacy 20
  rejected_by_ctc = "rejected_by_ctc", // legacy 21
  transfer_rejected_by_ctc = "transfer_rejected_by_ctc", // legacy 22
  transfer_rejected_by_customer = "transfer_rejected_by_customer", // legacy 27
  transfer_rejected_by_beneficiary = "transfer_rejected_by_beneficiary", // legacy 26 (purchase rejected by customer/beneficiary)
  sell_rejected_by_customer = "sell_rejected_by_customer", // legacy 23
  sell_rejected_by_ctc = "sell_rejected_by_ctc", // legacy 24
  sell_rejected_by_beneficiary = "sell_rejected_by_beneficiary", // legacy 25
  deleted = "deleted", // legacy 32
  cancelled = "cancelled", // legacy 40/41: cancelled after acceptance, before completion (reason required)
  cancelled_after_completion = "cancelled_after_completion", // legacy 42
}

/**
 * End of the Kuwait calendar day of `at` (= next Kuwait midnight). Kuwait is UTC+3 all year (no DST).
 * WhatsApp questions to the beneficiary are valid only on the day they were sent.
 */
export const ctcKuwaitDayEnd = (at: Date | number): Date => {
  const offset = 3 * 3600_000;
  const day = 86400_000;
  const t = typeof at === "number" ? at : at.getTime();
  return new Date(Math.floor((t + offset) / day) * day + day - offset);
};

/** Legacy `PurchaseOrderStatusEnum` values, for migration and cross-reference. */
export const CTC_LEGACY_STATUS_IDS: Record<CtcPoStatusCode, number[]> = {
  [CtcPoStatusCode.draft]: [1],
  [CtcPoStatusCode.waiting_customer_approval]: [2, 3],
  [CtcPoStatusCode.waiting_ctc_acceptance]: [4],
  [CtcPoStatusCode.waiting_purchase_confirmation]: [5, 6, 7],
  [CtcPoStatusCode.waiting_beneficiary_transfer_approval]: [13],
  [CtcPoStatusCode.waiting_customer_transfer_request]: [8],
  [CtcPoStatusCode.waiting_customer_transfer_approval]: [10],
  [CtcPoStatusCode.waiting_transfer_confirmation]: [9],
  [CtcPoStatusCode.waiting_beneficiary_sell_request]: [30],
  [CtcPoStatusCode.waiting_sell_request]: [11],
  [CtcPoStatusCode.waiting_sell_confirmation]: [12],
  [CtcPoStatusCode.waiting_collection]: [],
  [CtcPoStatusCode.completed]: [31],
  [CtcPoStatusCode.rejected_by_customer]: [20],
  [CtcPoStatusCode.rejected_by_ctc]: [21],
  [CtcPoStatusCode.transfer_rejected_by_ctc]: [22],
  [CtcPoStatusCode.transfer_rejected_by_customer]: [27],
  [CtcPoStatusCode.transfer_rejected_by_beneficiary]: [26],
  [CtcPoStatusCode.sell_rejected_by_customer]: [23],
  [CtcPoStatusCode.sell_rejected_by_ctc]: [24],
  [CtcPoStatusCode.sell_rejected_by_beneficiary]: [25],
  [CtcPoStatusCode.deleted]: [32],
  [CtcPoStatusCode.cancelled]: [40, 41],
  [CtcPoStatusCode.cancelled_after_completion]: [42],
};

/** Aggregate business status from the legacy `LK_PoState` table (what reports group by). */
export type CtcPoState = "Draft" | "Pending" | "Submitted" | "Accepted" | "Purchased" | "Transferred" | "Completed" | "Canceled" | "Deleted" | "Canceled after completed";

export const ctcPoStateOf = (status: CtcPoStatusCode): CtcPoState => {
  switch (status) {
    case CtcPoStatusCode.draft: return "Draft";
    case CtcPoStatusCode.waiting_customer_approval: return "Pending";
    case CtcPoStatusCode.waiting_ctc_acceptance: return "Submitted";
    case CtcPoStatusCode.waiting_purchase_confirmation: return "Accepted";
    case CtcPoStatusCode.waiting_beneficiary_transfer_approval:
    case CtcPoStatusCode.waiting_customer_transfer_request:
    case CtcPoStatusCode.waiting_customer_transfer_approval:
    case CtcPoStatusCode.waiting_transfer_confirmation: return "Purchased";
    case CtcPoStatusCode.waiting_beneficiary_sell_request:
    case CtcPoStatusCode.waiting_sell_request:
    case CtcPoStatusCode.waiting_sell_confirmation:
    case CtcPoStatusCode.waiting_collection: return "Transferred";
    case CtcPoStatusCode.completed: return "Completed";
    case CtcPoStatusCode.deleted: return "Deleted";
    case CtcPoStatusCode.cancelled_after_completion: return "Canceled after completed";
    default: return "Canceled";
  }
};

export enum CtcAction {
  submit = "submit", // maker: draft -> checker or CTC
  approve = "approve", // checker: approve the PO
  reject = "reject", // checker: reject the PO
  accept = "accept", // supervisor: accept + assign dealer
  reject_by_ctc = "reject_by_ctc", // supervisor/dealer: reject before acceptance (after it: cancel)
  confirm_purchase = "confirm_purchase", // dealer: broker, commodity, qty, price, certificate
  beneficiary_approve_transfer = "beneficiary_approve_transfer", // WhatsApp yes
  beneficiary_reject_transfer = "beneficiary_reject_transfer", // WhatsApp no
  request_transfer = "request_transfer", // maker: ownership transfer request
  approve_transfer = "approve_transfer", // checker
  reject_transfer = "reject_transfer", // checker
  confirm_transfer = "confirm_transfer", // dealer
  reject_transfer_by_ctc = "reject_transfer_by_ctc", // dealer
  beneficiary_sell = "beneficiary_sell", // beneficiary answered "sell" (WhatsApp or email)
  beneficiary_collect = "beneficiary_collect", // beneficiary answered "collect" (WhatsApp or email)
  beneficiary_reject_sell = "beneficiary_reject_sell", // WhatsApp explicit refusal
  request_sell = "request_sell", // maker/checker on behalf of beneficiary
  reject_sell_by_customer = "reject_sell_by_customer",
  confirm_sell = "confirm_sell", // dealer
  reject_sell_by_ctc = "reject_sell_by_ctc",
  complete_collection = "complete_collection", // dealer: goods handed over
  cancel_after_completion = "cancel_after_completion", // admin/back office
  cancel = "cancel", // supervisor / back office / admin: from Accepted until before Completed (reason required)
  delete = "delete", // maker while draft, admin until Submitted (before Accepted)
  reassign = "reassign", // supervisor: change assigned dealer, no status change
  beneficiary_change_answer = "beneficiary_change_answer", // reopen the WhatsApp question after a mistaken answer
}

/**
 * Who may perform an action. Maps 1:1 to the ctc_* custom claims, plus the WhatsApp beneficiary.
 * `viewer` (ctc_viewer) sees orders, transactions and reports but may not take any action.
 */
export type CtcRole = "maker" | "checker" | "supervisor" | "dealer" | "backoffice" | "admin" | "beneficiary" | "viewer";

/** Customer workflow flags (legacy `TbCustomer` booleans passed to Camunda as process variables). */
export interface CtcCustomerFlags {
  needInternalApproval?: boolean; // legacy NeedInternalAprroval
  needInternalTransferApproval?: boolean;
  needBeneficiaryTransferApproval?: boolean;
  sellOnBehalfOfBeneficiary?: boolean; // customer sells for the beneficiary (no WhatsApp sell prompt)
  ctcSellOnBehalfOfCustomer?: boolean; // CTC sells directly after transfer
  isTreasury?: boolean; // enables value date on the order
}

export interface CtcTransitionInput {
  status: CtcPoStatusCode;
  action: CtcAction;
  role: CtcRole;
  customer: CtcCustomerFlags;
  /** The action that produced the current status (po.lastAction.action). Needed only for beneficiary_change_answer. */
  lastAction?: CtcAction;
}

export interface CtcTransitionResult {
  next: CtcPoStatusCode;
  /** Side effects the executor must run after the status write. */
  effects: CtcEffect[];
}

export type CtcEffect =
  | "generate_attachment_1" // purchase request / approval PDF
  | "generate_attachment_2" // purchase confirmation PDF
  | "generate_attachment_3" // ownership transfer request PDF
  | "generate_attachment_4" // ownership transfer confirmation PDF
  | "generate_attachment_5" // sell confirmation PDF
  | "calculate_commissions"
  | "create_settlement"
  | "whatsapp_reminder" // repeat the same request, preserving its identity
  | "whatsapp_purchase_details" // purchase confirmation before consent
  | "whatsapp_transfer_approval" // ask beneficiary yes/no
  | "whatsapp_sell_request" // ask beneficiary sell/collect
  | "whatsapp_transfer_confirmed" // executed copy of attachment 4
  | "whatsapp_sell_confirmed" // executed copy of attachment 5
  | "close_pending_reply" // cancel any open WhatsApp decision for this PO
  | "notify_next_lane";

export class CtcTransitionError extends Error {
  constructor(public readonly code: "invalid_action" | "forbidden_role", message: string) {
    super(message);
    this.name = "CtcTransitionError";
  }
}

const allow = (roles: CtcRole[], role: CtcRole, action: CtcAction) => {
  if (role === "admin" && action !== CtcAction.beneficiary_approve_transfer && action !== CtcAction.beneficiary_reject_transfer &&
    action !== CtcAction.beneficiary_sell && action !== CtcAction.beneficiary_collect && action !== CtcAction.beneficiary_reject_sell) {
    return; // admin can do any staff action (audited); beneficiary answers are recorded via the manual path instead
  }
  if (!roles.includes(role)) {
    throw new CtcTransitionError("forbidden_role", `Role ${role} may not ${action}`);
  }
};

const invalid = (status: CtcPoStatusCode, action: CtcAction): never => {
  throw new CtcTransitionError("invalid_action", `Action ${action} is not valid while ${status}`);
};

/*
 * Sharia: the beneficiary is always part of the buy and the sell, so the WhatsApp questions ALWAYS go (transfer
 * approval with Attachment 2, sell / collect with Attachment 4). The customer settings only add portal buttons at
 * those steps; whichever answers first, the beneficiary on WhatsApp or the portal user, moves the order and the
 * other is closed (staff actions close the open question):
 *  - needBeneficiaryTransferApproval OFF: the maker / checker may also Request transfer at the transfer question.
 *  - sellOnBehalfOfBeneficiary ON: the maker / checker may also Request sale at the sell question.
 *  - ctcSellOnBehalfOfCustomer ON: the dealer may also Confirm sale at the sell question.
 * waiting_customer_transfer_request / waiting_sell_request are still reached (after the beneficiary's Yes, and by
 * orders that were already there).
 */

/** The status after a confirmed purchase: always the beneficiary's WhatsApp transfer approval. */
const afterPurchase = (): CtcTransitionResult =>
  ({ next: CtcPoStatusCode.waiting_beneficiary_transfer_approval, effects: ["whatsapp_purchase_details", "whatsapp_transfer_approval"] });

/** The status after the transfer is confirmed by CTC: always the beneficiary's WhatsApp sell / collect question. */
const afterTransfer = (): CtcTransitionResult =>
  ({ next: CtcPoStatusCode.waiting_beneficiary_sell_request, effects: ["generate_attachment_4", "whatsapp_transfer_confirmed", "whatsapp_sell_request"] });

/** The bank requests the ownership transfer: to its checker first if the customer requires that. */
const transferRequested = (c: CtcCustomerFlags, extra: CtcEffect[] = []): CtcTransitionResult =>
  c.needInternalTransferApproval
    ? { next: CtcPoStatusCode.waiting_customer_transfer_approval, effects: [...extra, "generate_attachment_3", "notify_next_lane"] }
    : { next: CtcPoStatusCode.waiting_transfer_confirmation, effects: [...extra, "generate_attachment_3", "notify_next_lane"] };

/** CTC confirms the sale: the order completes and its settlement opens. */
const saleConfirmed = (extra: CtcEffect[] = []): CtcTransitionResult =>
  ({ next: CtcPoStatusCode.completed, effects: [...extra, "create_settlement", "generate_attachment_5", "whatsapp_sell_confirmed"] });

/**
 * Resolve the next status for an action, or throw `CtcTransitionError`.
 * Pure: the executor (functions-ctc) is responsible for persisting and for the effects.
 */
/**
 * Where each beneficiary answer lands, so a mistaken answer can be undone only while the order
 * is still exactly where that answer put it (nobody has acted on it since).
 */
const BENEFICIARY_ANSWER_LANDING: Partial<Record<CtcAction, { status: CtcPoStatusCode; reopen: CtcPoStatusCode; prompt: CtcEffect }>> = {
  [CtcAction.beneficiary_approve_transfer]: { status: CtcPoStatusCode.waiting_customer_transfer_request, reopen: CtcPoStatusCode.waiting_beneficiary_transfer_approval, prompt: "whatsapp_transfer_approval" },
  [CtcAction.beneficiary_reject_transfer]: { status: CtcPoStatusCode.transfer_rejected_by_beneficiary, reopen: CtcPoStatusCode.waiting_beneficiary_transfer_approval, prompt: "whatsapp_transfer_approval" },
  [CtcAction.beneficiary_sell]: { status: CtcPoStatusCode.waiting_sell_confirmation, reopen: CtcPoStatusCode.waiting_beneficiary_sell_request, prompt: "whatsapp_sell_request" },
  [CtcAction.beneficiary_collect]: { status: CtcPoStatusCode.waiting_collection, reopen: CtcPoStatusCode.waiting_beneficiary_sell_request, prompt: "whatsapp_sell_request" },
  [CtcAction.beneficiary_reject_sell]: { status: CtcPoStatusCode.sell_rejected_by_beneficiary, reopen: CtcPoStatusCode.waiting_beneficiary_sell_request, prompt: "whatsapp_sell_request" },
};


/** A document the assigned dealer uploads with an action; each may have several files. */
export interface CtcDealerUploadSpec {
  kind: string; // stable id stored on each attachment
  label: string;
  required?: boolean;
}

/** Dealer uploads per action; every file is stored in po.attachments under the action's number. */
/** Attachment numbers of the dealer's uploads (1-5 are the generated PDFs). */
export type CtcDealerUploadNumber = 6 | 7 | 8 | 9;
/** po.attachments number of the other documents anyone working on the order adds on the order page (ctcAddAttachments). */
export const CTC_OTHER_DOCUMENT_NUMBER = 10;
export const CTC_DEALER_UPLOADS: Partial<Record<CtcAction, { number: CtcDealerUploadNumber; documents: CtcDealerUploadSpec[] }>> = {
  [CtcAction.confirm_purchase]: { number: 6, documents: [{ kind: "vendor_confirmation", label: "Vendor purchase confirmation", required: true }] },
  [CtcAction.confirm_sell]: { number: 7, documents: [{ kind: "sell_confirmation", label: "Sell confirmation", required: true }] },
  [CtcAction.confirm_transfer]: { number: 8, documents: [{ kind: "transfer_confirmation", label: "Transfer confirmation", required: true }] },
  // The dealer recording the beneficiary's sale instruction (phone, branch): proof of that instruction
  [CtcAction.beneficiary_sell]: { number: 9, documents: [{ kind: "sale_authorisation", label: "Client sale authorisation", required: true }] },
};

export const CTC_MAX_DEALER_UPLOAD_FILES = 10;

export function ctcTransition({ status, action, role, customer, lastAction }: CtcTransitionInput): CtcTransitionResult {
  if (role === "viewer") throw new CtcTransitionError("forbidden_role", "View-only access: no actions");
  if (action === CtcAction.beneficiary_change_answer) {
    allow(["beneficiary", "dealer"], role, action);
    const landing = lastAction ? BENEFICIARY_ANSWER_LANDING[lastAction] : undefined;
    if (!landing || landing.status !== status) return invalid(status, action);
    return { next: landing.reopen, effects: [landing.prompt] };
  }
  // Actions that are valid from many states.
  if (action === CtcAction.reassign) {
    allow(["supervisor"], role, action);
    if (isTerminal(status) || status === CtcPoStatusCode.draft) return invalid(status, action);
    return { next: status, effects: ["notify_next_lane"] };
  }
  if (action === CtcAction.delete) {
    // Only until the order is accepted; from then on it is cancelled (Cancel), which keeps it in the records
    if (!CTC_DELETABLE_STATUSES.has(status)) return invalid(status, action);
    if (status === CtcPoStatusCode.draft) {
      allow(["maker"], role, action);
    } else {
      allow([], role, action); // admin only
    }
    return { next: CtcPoStatusCode.deleted, effects: ["close_pending_reply"] };
  }
  if (action === CtcAction.cancel) {
    // Accepted … before Completed. Commissions / settlements already created stay for back office to handle.
    if (!CTC_CANCELLABLE_STATUSES.has(status)) return invalid(status, action);
    allow(["supervisor", "backoffice"], role, action); // + admin
    return { next: CtcPoStatusCode.cancelled, effects: ["close_pending_reply"] };
  }

  switch (status) {
    case CtcPoStatusCode.draft:
      if (action !== CtcAction.submit) return invalid(status, action);
      allow(["maker"], role, action);
      return customer.needInternalApproval
        ? { next: CtcPoStatusCode.waiting_customer_approval, effects: ["notify_next_lane"] }
        : { next: CtcPoStatusCode.waiting_ctc_acceptance, effects: ["generate_attachment_1", "notify_next_lane"] };

    case CtcPoStatusCode.waiting_customer_approval:
      if (action === CtcAction.approve) {
        allow(["checker"], role, action);
        return { next: CtcPoStatusCode.waiting_ctc_acceptance, effects: ["generate_attachment_1", "notify_next_lane"] };
      }
      if (action === CtcAction.reject) {
        allow(["checker"], role, action);
        return { next: CtcPoStatusCode.rejected_by_customer, effects: [] };
      }
      return invalid(status, action);

    case CtcPoStatusCode.waiting_ctc_acceptance:
      if (action === CtcAction.accept) {
        allow(["supervisor"], role, action);
        return { next: CtcPoStatusCode.waiting_purchase_confirmation, effects: ["notify_next_lane"] };
      }
      if (action === CtcAction.reject_by_ctc) {
        allow(["supervisor", "dealer"], role, action);
        return { next: CtcPoStatusCode.rejected_by_ctc, effects: [] };
      }
      return invalid(status, action);

    case CtcPoStatusCode.waiting_purchase_confirmation:
      if (action === CtcAction.confirm_purchase) {
        allow(["dealer"], role, action);
        const r = afterPurchase();
        // Commissions are known once bought; the settlement (Transactions) opens only when the order completes
        return { next: r.next, effects: ["calculate_commissions", "generate_attachment_2", ...r.effects] };
      }
      // No reject once accepted: the order is stopped with Cancel (CTC_CANCELLABLE_STATUSES)
      return invalid(status, action);

    case CtcPoStatusCode.waiting_beneficiary_transfer_approval:
      if (action === CtcAction.beneficiary_approve_transfer) {
        allow(["beneficiary", "dealer"], role, action); // dealer = recorded manually after a phone call
        return { next: CtcPoStatusCode.waiting_customer_transfer_request, effects: ["close_pending_reply", "notify_next_lane"] };
      }
      if (action === CtcAction.beneficiary_reject_transfer) {
        allow(["beneficiary", "dealer"], role, action);
        return { next: CtcPoStatusCode.transfer_rejected_by_beneficiary, effects: ["close_pending_reply"] };
      }
      // Transfer approval not required by the customer: the bank may request the transfer without waiting
      if (action === CtcAction.request_transfer && !customer.needBeneficiaryTransferApproval) {
        allow(["maker", "checker"], role, action);
        return transferRequested(customer, ["close_pending_reply"]);
      }
      return invalid(status, action);

    case CtcPoStatusCode.waiting_customer_transfer_request:
      if (action === CtcAction.request_transfer) {
        allow(["maker", "checker"], role, action);
        return transferRequested(customer);
      }
      if (action === CtcAction.reject_transfer) {
        allow(["maker", "checker"], role, action);
        return { next: CtcPoStatusCode.transfer_rejected_by_customer, effects: [] };
      }
      return invalid(status, action);

    case CtcPoStatusCode.waiting_customer_transfer_approval:
      if (action === CtcAction.approve_transfer) {
        allow(["checker"], role, action);
        return { next: CtcPoStatusCode.waiting_transfer_confirmation, effects: ["notify_next_lane"] };
      }
      if (action === CtcAction.reject_transfer) {
        allow(["checker"], role, action);
        return { next: CtcPoStatusCode.transfer_rejected_by_customer, effects: [] };
      }
      return invalid(status, action);

    case CtcPoStatusCode.waiting_transfer_confirmation:
      if (action === CtcAction.confirm_transfer) {
        allow(["dealer"], role, action);
        return afterTransfer();
      }
      if (action === CtcAction.reject_transfer_by_ctc) {
        allow(["dealer"], role, action);
        return { next: CtcPoStatusCode.transfer_rejected_by_ctc, effects: [] };
      }
      return invalid(status, action);

    case CtcPoStatusCode.waiting_beneficiary_sell_request:
      if (action === CtcAction.beneficiary_sell) {
        allow(["beneficiary", "dealer"], role, action);
        return { next: CtcPoStatusCode.waiting_sell_confirmation, effects: ["close_pending_reply", "notify_next_lane"] };
      }
      if (action === CtcAction.beneficiary_collect) {
        allow(["beneficiary", "dealer"], role, action);
        return { next: CtcPoStatusCode.waiting_collection, effects: ["close_pending_reply", "notify_next_lane"] };
      }
      if (action === CtcAction.beneficiary_reject_sell) {
        allow(["beneficiary", "dealer"], role, action);
        return { next: CtcPoStatusCode.sell_rejected_by_beneficiary, effects: ["close_pending_reply"] };
      }
      // Customer sells on behalf of the beneficiary: the bank may request the sale without waiting
      if (action === CtcAction.request_sell && customer.sellOnBehalfOfBeneficiary) {
        allow(["maker", "checker"], role, action);
        return { next: CtcPoStatusCode.waiting_sell_confirmation, effects: ["close_pending_reply", "notify_next_lane"] };
      }
      // CTC sells on behalf of the customer: the dealer may confirm the sale without waiting
      if (action === CtcAction.confirm_sell && customer.ctcSellOnBehalfOfCustomer) {
        allow(["dealer"], role, action);
        return saleConfirmed(["close_pending_reply"]);
      }
      return invalid(status, action);

    case CtcPoStatusCode.waiting_sell_request:
      if (action === CtcAction.request_sell) {
        allow(["maker", "checker"], role, action);
        return { next: CtcPoStatusCode.waiting_sell_confirmation, effects: ["notify_next_lane"] };
      }
      if (action === CtcAction.reject_sell_by_customer) {
        allow(["maker", "checker"], role, action);
        return { next: CtcPoStatusCode.sell_rejected_by_customer, effects: [] };
      }
      return invalid(status, action);

    case CtcPoStatusCode.waiting_sell_confirmation:
      if (action === CtcAction.confirm_sell) {
        allow(["dealer"], role, action);
        return saleConfirmed();
      }
      if (action === CtcAction.reject_sell_by_ctc) {
        allow(["dealer"], role, action);
        return { next: CtcPoStatusCode.sell_rejected_by_ctc, effects: [] };
      }
      return invalid(status, action);

    case CtcPoStatusCode.waiting_collection:
      if (action === CtcAction.complete_collection) {
        allow(["dealer"], role, action);
        return { next: CtcPoStatusCode.completed, effects: ["create_settlement"] };
      }
      return invalid(status, action);

    case CtcPoStatusCode.completed:
      if (action === CtcAction.cancel_after_completion) {
        allow(["backoffice"], role, action);
        return { next: CtcPoStatusCode.cancelled_after_completion, effects: [] };
      }
      return invalid(status, action);

    default:
      return invalid(status, action);
  }
}

/**
 * Delete: only before the order is accepted (draft: maker; the rest: admin), plus admin clean-up of
 * orders rejected by the checker or by CTC. Transfer / sale rejections happen after acceptance and stay.
 */
export const CTC_DELETABLE_STATUSES: ReadonlySet<CtcPoStatusCode> = new Set([
  CtcPoStatusCode.draft,
  CtcPoStatusCode.waiting_customer_approval,
  CtcPoStatusCode.waiting_ctc_acceptance,
  CtcPoStatusCode.rejected_by_customer,
  CtcPoStatusCode.rejected_by_ctc,
]);
/** Cancel: from Accepted (purchase not yet confirmed) until before Completed. */
export const CTC_CANCELLABLE_STATUSES: ReadonlySet<CtcPoStatusCode> = new Set([
  CtcPoStatusCode.waiting_purchase_confirmation,
  CtcPoStatusCode.waiting_beneficiary_transfer_approval,
  CtcPoStatusCode.waiting_customer_transfer_request,
  CtcPoStatusCode.waiting_customer_transfer_approval,
  CtcPoStatusCode.waiting_transfer_confirmation,
  CtcPoStatusCode.waiting_beneficiary_sell_request,
  CtcPoStatusCode.waiting_sell_request,
  CtcPoStatusCode.waiting_sell_confirmation,
  CtcPoStatusCode.waiting_collection,
]);

export const CTC_TERMINAL_STATUSES: ReadonlySet<CtcPoStatusCode> = new Set([
  CtcPoStatusCode.completed,
  CtcPoStatusCode.rejected_by_customer,
  CtcPoStatusCode.rejected_by_ctc,
  CtcPoStatusCode.transfer_rejected_by_ctc,
  CtcPoStatusCode.transfer_rejected_by_customer,
  CtcPoStatusCode.transfer_rejected_by_beneficiary,
  CtcPoStatusCode.sell_rejected_by_customer,
  CtcPoStatusCode.sell_rejected_by_ctc,
  CtcPoStatusCode.sell_rejected_by_beneficiary,
  CtcPoStatusCode.deleted,
  CtcPoStatusCode.cancelled,
  CtcPoStatusCode.cancelled_after_completion,
]);

export const isTerminal = (status: CtcPoStatusCode) => CTC_TERMINAL_STATUSES.has(status);

/** Which lane a status is waiting on — drives the work queue and the sidebar badge. */
export const ctcLaneOf = (status: CtcPoStatusCode): CtcRole | null => {
  switch (status) {
    case CtcPoStatusCode.draft:
    case CtcPoStatusCode.waiting_customer_transfer_request:
    case CtcPoStatusCode.waiting_sell_request:
      return "maker";
    case CtcPoStatusCode.waiting_customer_approval:
    case CtcPoStatusCode.waiting_customer_transfer_approval:
      return "checker";
    case CtcPoStatusCode.waiting_ctc_acceptance:
      return "supervisor";
    case CtcPoStatusCode.waiting_purchase_confirmation:
    case CtcPoStatusCode.waiting_transfer_confirmation:
    case CtcPoStatusCode.waiting_sell_confirmation:
    case CtcPoStatusCode.waiting_collection:
      return "dealer";
    case CtcPoStatusCode.waiting_beneficiary_transfer_approval:
    case CtcPoStatusCode.waiting_beneficiary_sell_request:
      return "beneficiary";
    default:
      return null;
  }
};

/** Actions a role can take from a status, for rendering an action bar without try/catch loops. */
export const ctcAvailableActions = (status: CtcPoStatusCode, role: CtcRole, customer: CtcCustomerFlags, lastAction?: CtcAction): CtcAction[] =>
  Object.values(CtcAction).filter((action) => {
    try {
      ctcTransition({ status, action, role, customer, lastAction });
      return true;
    } catch (e) {
      if (e instanceof CtcTransitionError) return false;
      throw e;
    }
  });

/** Display metadata per status, matching the portal's `Tag` shape ({ code, title, badgeColor }). */
export const CTC_PO_STATUS_TAGS: Record<CtcPoStatusCode, { code: CtcPoStatusCode; title: string; badgeColor: string }> = {
  [CtcPoStatusCode.draft]: { code: CtcPoStatusCode.draft, title: "Draft", badgeColor: "gray" },
  [CtcPoStatusCode.waiting_customer_approval]: { code: CtcPoStatusCode.waiting_customer_approval, title: "Awaiting customer approval", badgeColor: "yellow" },
  [CtcPoStatusCode.waiting_ctc_acceptance]: { code: CtcPoStatusCode.waiting_ctc_acceptance, title: "Awaiting CTC acceptance", badgeColor: "yellow" },
  [CtcPoStatusCode.waiting_purchase_confirmation]: { code: CtcPoStatusCode.waiting_purchase_confirmation, title: "Awaiting purchase confirmation", badgeColor: "blue" },
  [CtcPoStatusCode.waiting_beneficiary_transfer_approval]: { code: CtcPoStatusCode.waiting_beneficiary_transfer_approval, title: "Awaiting beneficiary approval", badgeColor: "green" },
  [CtcPoStatusCode.waiting_customer_transfer_request]: { code: CtcPoStatusCode.waiting_customer_transfer_request, title: "Awaiting customer transfer request", badgeColor: "yellow" },
  [CtcPoStatusCode.waiting_customer_transfer_approval]: { code: CtcPoStatusCode.waiting_customer_transfer_approval, title: "Awaiting customer transfer approval", badgeColor: "yellow" },
  [CtcPoStatusCode.waiting_transfer_confirmation]: { code: CtcPoStatusCode.waiting_transfer_confirmation, title: "Awaiting transfer confirmation", badgeColor: "blue" },
  [CtcPoStatusCode.waiting_beneficiary_sell_request]: { code: CtcPoStatusCode.waiting_beneficiary_sell_request, title: "Waiting Sell Request", badgeColor: "green" },
  [CtcPoStatusCode.waiting_sell_request]: { code: CtcPoStatusCode.waiting_sell_request, title: "Awaiting sale request", badgeColor: "yellow" },
  [CtcPoStatusCode.waiting_sell_confirmation]: { code: CtcPoStatusCode.waiting_sell_confirmation, title: "Awaiting sale confirmation", badgeColor: "blue" },
  [CtcPoStatusCode.waiting_collection]: { code: CtcPoStatusCode.waiting_collection, title: "Awaiting collection", badgeColor: "blue" },
  [CtcPoStatusCode.completed]: { code: CtcPoStatusCode.completed, title: "Completed", badgeColor: "teal" },
  [CtcPoStatusCode.rejected_by_customer]: { code: CtcPoStatusCode.rejected_by_customer, title: "Rejected by customer", badgeColor: "red" },
  [CtcPoStatusCode.rejected_by_ctc]: { code: CtcPoStatusCode.rejected_by_ctc, title: "Rejected by CTC", badgeColor: "red" },
  [CtcPoStatusCode.transfer_rejected_by_ctc]: { code: CtcPoStatusCode.transfer_rejected_by_ctc, title: "Transfer rejected by CTC", badgeColor: "red" },
  [CtcPoStatusCode.transfer_rejected_by_customer]: { code: CtcPoStatusCode.transfer_rejected_by_customer, title: "Transfer rejected by customer", badgeColor: "red" },
  [CtcPoStatusCode.transfer_rejected_by_beneficiary]: { code: CtcPoStatusCode.transfer_rejected_by_beneficiary, title: "Transfer rejected by beneficiary", badgeColor: "red" },
  [CtcPoStatusCode.sell_rejected_by_customer]: { code: CtcPoStatusCode.sell_rejected_by_customer, title: "Sale rejected by customer", badgeColor: "red" },
  [CtcPoStatusCode.sell_rejected_by_ctc]: { code: CtcPoStatusCode.sell_rejected_by_ctc, title: "Sale rejected by CTC", badgeColor: "red" },
  [CtcPoStatusCode.sell_rejected_by_beneficiary]: { code: CtcPoStatusCode.sell_rejected_by_beneficiary, title: "Sale rejected by beneficiary", badgeColor: "red" },
  [CtcPoStatusCode.deleted]: { code: CtcPoStatusCode.deleted, title: "Deleted", badgeColor: "gray" },
  [CtcPoStatusCode.cancelled]: { code: CtcPoStatusCode.cancelled, title: "Cancelled", badgeColor: "red" },
  [CtcPoStatusCode.cancelled_after_completion]: { code: CtcPoStatusCode.cancelled_after_completion, title: "Cancelled after completion", badgeColor: "orange" },
};
